const CAP16 = {
  "id": 42,
  "title": "Cómo funciona un monedero Bitcoin",
  "subtitle": "Curso Bitcoin 101",
  "sections": [
    {
      "heading": "¿Qué es un monedero (Wallet)?",
      "text": [
        "Uno de los elementos esenciales para operar con cualquier criptomoneda es el monedero (wallet en inglés).",
        "El término wallet hace referencia a una cartera, billetera o monedero virtual en el que podemos recibir criptomonedas, comprobar el saldo e historial de transacciones y, por supuesto, enviar criptomonedas.",
        "Es un software o hardware diseñado exclusivamente para almacenar y gestionar las claves públicas y claves privadas de nuestras criptomonedas."
      ]
    },
    {
      "heading": "Funciones principales de un monedero Bitcoin",
      "list": [
        "Crear direcciones para recibir criptomonedas.",
        "Almacenar tus claves privadas y claves públicas.",
        "Enviar criptomonedas a otras direcciones.",
        "Mostrar el saldo de tus criptomonedas.",
        "Registrar y mostrar el historial de transacciones."
      ]
    },
    {
      "heading": "Direcciones y seguridad",
      "text": [
        "El monedero es el encargado de crear las direcciones, las cuales se utilizan siempre que desees que alguien te envíe criptomonedas.",
        "Estas direcciones no revelan tu identidad, sino que representan tu clave pública de manera codificada.",
        "El monedero también guarda tu clave privada de forma segura, la cual es esencial para firmar transacciones."
      ]
    },
    {
      "heading": "Tipos de monederos",
      "text": [
        "Los monederos, generalmente, son programas de ordenador o aplicaciones para smartphones.",
        "No obstante, para los usuarios con pocos conocimientos acerca del funcionamiento de las criptomonedas lo más recomendable es utilizar servicios confiables y verificados que ofrezcan monederos en la nube."
      ],
      "subsections": [
        {
          "subheading": "Monederos de software",
          "text": [
            "Se instalan en ordenadores o smartphones.",
            "Permiten un control directo sobre tus claves privadas.",
            "Ofrecen una buena combinación de seguridad y facilidad de uso."
          ]
        },
        {
          "subheading": "Monederos en la nube",
          "text": [
            "Se accede a ellos desde una web o aplicación, y las claves se almacenan de forma segura en servidores protegidos.",
            "Son ideales para principiantes.",
            "Permite gestionar múltiples criptomonedas desde un mismo lugar."
          ]
        },
        {
          "subheading": "Monederos hardware",
          "text": [
            "Son dispositivos físicos especialmente diseñados para guardar claves privadas de forma aislada.",
            "Ofrecen el nivel más alto de seguridad.",
            "Ideales para almacenar grandes cantidades de criptomonedas."
          ]
        }
      ]
    },
    {
      "heading": "Gestión de múltiples criptomonedas",
      "text": [
        "Cada criptomoneda tiene un monedero que has de instalarte. Esto, además de ser muy incómodo para el usuario, puede generar el problema de aprender a identificar las diferentes apariencias y funcionalidades de cada monedero, junto a los aspectos de seguridad.",
        "Por ese motivo, La Wallet es una solución que te permite gestionar diferentes criptomonedas desde un mismo lugar: recibir, almacenar, enviar bitcoin, comprobar saldo, pero también comprar, vender e intercambiar criptomonedas."
      ]
    },
    {
      "heading": "Ventajas de un monedero en la nube",
      "text": [
        "Un monedero en la nube, mantiene la seguridad del monedero, evitando que tengas que preocuparte por ello o que tengas que respaldar los datos periódicamente.",
        "Permite acceder a tus criptomonedas desde cualquier dispositivo.",
        "Simplifica la experiencia del usuario y mejora la protección frente a pérdidas o fallos técnicos."
      ]
    },
     {
      "heading": "¿Qué es un monedero multifirma?",
      "text": [
        "Un monedero multifirma (multisig wallet) es un tipo de monedero Bitcoin que permite que varias personas gestionen una misma dirección, pero donde no basta con una sola firma para aprobar una transacción.",
        "Este sistema funciona mediante un mecanismo de firmado criptográfico múltiple que requiere la participación de varias claves privadas para autorizar el envío de fondos.",
        "Gracias a este modelo, los fondos están bajo un control colectivo, aumentando significativamente la seguridad, ya que un atacante tendría que comprometer múltiples claves privadas para robar los fondos."
      ],
      "analogy": "Es como una caja fuerte que requiere varias llaves para ser abierta. Puedes configurarla para que se necesiten 2 de 3 llaves, 3 de 5, o cualquier combinación que tú decidas."
    },
    {
      "heading": "¿Cómo funciona el mecanismo multifirma?",
      "text": [
        "En una transacción Bitcoin tradicional, basta con una sola firma privada para autorizar la operación.",
        "En cambio, en una dirección multifirma, se necesitan varias firmas criptográficas para que la red acepte la transacción como válida.",
        "Por ejemplo, puedes crear una dirección multifirma 2 de 3, donde existen tres propietarios, pero solamente se requieren dos firmas (cualesquiera) para autorizar una transacción.",
        "Este sistema se conoce como modelo M-de-N, donde M es el número mínimo de firmas necesarias, y N es el número total de participantes."
      ],
      "list": [
        "2 de 3: Se necesitan dos firmas de tres posibles.",
        "3 de 5: Se necesitan tres firmas de cinco posibles.",
        "2 de 2: Se necesitan ambas firmas obligatoriamente."
      ]
    },
    {
      "heading": "Ventajas de los monederos multifirma",
      "list": [
        "🔐 Mayor seguridad: Evita que una sola persona pueda mover los fondos sin autorización.",
        "👥 Control colectivo: Ideal para empresas, fundaciones, familias o inversiones en grupo.",
        "🛡️ Protege contra robos: Un atacante necesitaría robar varias claves privadas.",
        "⚙️ Configuración flexible: Puedes elegir cuántas firmas se necesitan (M de N).",
        "⚖️ Transparencia y confianza: Ninguna persona puede actuar unilateralmente."
      ]
    },
    {
      "heading": "¿Cómo se crea una dirección multifirma?",
      "text": [
        "Si tu monedero permite crear direcciones multifirma, puedes configurarlo fácilmente.",
        "Si no, el proceso técnico sería el siguiente:"
      ],
      "steps": [
        "1️⃣ Crear las direcciones Bitcoin individuales para cada participante.",
        "2️⃣ Compartir únicamente las claves públicas (NO las claves privadas).",
        "3️⃣ Con estas claves públicas, generar una dirección multifirma P2SH (comienza por '3').",
        "4️⃣ Una persona inicia una transacción y la firma con su clave privada.",
        "5️⃣ La transacción firmada se comparte con otro firmante para completar las firmas requeridas.",
        "6️⃣ Cuando se alcanzan las firmas mínimas, se envía a la red Bitcoin donde será validada."
      ]
    },
    {
      "heading": "Características técnicas clave",
      "list": [
        "Las direcciones multifirma usan el estándar P2SH (Pay To Script Hash).",
        "Generalmente comienzan con el número '3'.",
        "Utilizan únicamente las claves públicas para generar la dirección compartida.",
        "Las claves privadas nunca se comparten, cada firmante conserva la suya en privado.",
        "El orden de las firmas no importa, siempre que se alcancen las necesarias."
      ]
    },
    {
      "heading": "¿Dónde se utilizan los monederos multifirma?",
      "list": [
        "🧾 Gobiernos y organismos públicos que necesitan transparencia.",
        "🏛️ Empresas o startups que manejan tesorería en Bitcoin.",
        "🌐 Exchanges y proveedores de servicios cripto.",
        "👪 Familias, herencias digitales y planes sucesorios.",
        "🤝 Inversiones en grupo y fondos comunitarios."
      ]
    },
    {
      "heading": "Resumen",
      "list": [
        "Un monedero multifirma permite que varias personas autoricen una transacción conjuntamente.",
        "Mejora la seguridad, la transparencia y evita el control individual sobre los fondos.",
        "Se basa en el sistema M-de-N (por ejemplo, 2 de 3).",
        "Las claves privadas nunca se comparten, solo las claves públicas.",
        "Es una herramienta ideal para empresas, familias, organizaciones y fondos conjuntos."
      ]
    },
    {
      "heading": "¿Quieres probar uno?",
      "text": [
        "Existen wallets como Bit2Me, Electrum, Coinbase Wallet o Sparrow Wallet que permiten crear direcciones multifirma de manera sencilla, sin necesidad de conocimientos técnicos avanzados."
      ]
    }


  ]
}


export default CAP16;
